import { useEffect, useState } from 'react'
import { useHealthRepository } from '@/app/AppProviders'
import { localDayAt, nextLocalDayStart } from '@/domain/health/time'
import type { IntakeRecord, SavedFood } from '@/domain/health/nutrition'

export function useNutritionToday(timeZone: string, referenceAt?: string) {
  const [today, setToday] = useState(() => localDayAt(referenceAt ?? new Date().toISOString(), timeZone))
  useEffect(() => {
    if (referenceAt) return
    const refresh = () => setToday(localDayAt(new Date().toISOString(), timeZone))
    const now = new Date().toISOString()
    const timer = setTimeout(refresh, Math.max(100, Date.parse(nextLocalDayStart(now, timeZone)) - Date.now() + 1))
    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', refresh)
    return () => { clearTimeout(timer); window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', refresh) }
  }, [today, timeZone, referenceAt])
  return today
}
export function useNutrition() {
  const repository = useHealthRepository()
  const [data, setData] = useState<{ records: IntakeRecord[]; saved: SavedFood[] } | null>(null)
  const [error, setError] = useState(false)
  const [revision, setRevision] = useState(0)
  useEffect(() => {
    let active = true
    void (async () => {
      try {
        const [records, saved] = await Promise.all([repository.getIntakeHistory(), repository.getSavedFoods()])
        if (active) { setData({ records, saved }); setError(false) }
      } catch { if (active) setError(true) }
    })()
    return () => { active = false }
  }, [repository, revision])
  return { data, setData, error, reload: () => setRevision((v) => v + 1) }
}
